import { FunctionComponent, useState, useEffect } from "react";
import { createLobby, createPublicLobby, findLobby, deleteLobbyByCode, getDB } from "./Firebase";
import { doc, getDoc } from "firebase/firestore";
import { GameMode } from "./Types";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { resetLocalStore } from "./Utils";
import { setGameMode, setShowMatchSetupScreen } from "./store/settingsSlice";
import { setWipeTransition } from "./store/wipeTransitionSlice";
import { getRankLabel } from "./services/FirebaseUsers";
import UserProfilePanel from "./UserProfilePanel";
import LobbyBrowser from "./LobbyBrowser";
import LeaderboardPanel from "./LeaderboardPanel";
import FriendsPanel from "./FriendsPanel";
import DailyBonus from "./DailyBonus";
import WalletPanel from "./WalletPanel";
import AchievementsPanel from "./AchievementsPanel";
import NewsPanel from "./NewsPanel";
import SupportPanel from "./SupportPanel";

const MainMenu: FunctionComponent = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userProfile = useAppSelector((state) => state.user.profile);

  const [isCreatingLobby, setIsCreatingLobby] = useState(false);
  const [isDeletingRoom, setIsDeletingRoom] = useState(false);
  const [activeGameCode, setActiveGameCode] = useState<string | null>(null);
  const [contact, setContact] = useState<{telegram?: string; email?: string} | null>(null);
  const [hasUnreadNews, setHasUnreadNews] = useState(false);

  // Загружаем контакт из Firestore
  useEffect(() => {
    getDoc(doc(getDB(), "settings", "contacts"))
      .then(snap => { if (snap.exists()) setContact(snap.data() as any); })
      .catch(() => {});
  }, []);

  // Проверяем есть ли непрочитанные новости
  useEffect(() => {
    fetch("/api/news")
      .then((r) => r.ok ? r.json() : [])
      .then((items: any[]) => {
        if (!Array.isArray(items) || items.length === 0) return;
        const latestTs = items[0]?.createdAt ?? 0;
        const lastRead = Number(localStorage.getItem("bg_news_last_read") || "0");
        setHasUnreadNews(latestTs > lastRead);
      })
      .catch(() => {});
  }, []);

  const openNews = () => {
    setShowNews(true);
    localStorage.setItem("bg_news_last_read", String(Date.now()));
    setHasUnreadNews(false);
  };

  // Проверяем есть ли активная игра при возврате в меню
  useEffect(() => {
    const lastCode = localStorage.getItem("ng_last_room");
    if (!lastCode || !userProfile) return;
    findLobby(lastCode).then(docRef => {
      if (docRef) setActiveGameCode(lastCode);
      else { localStorage.removeItem("ng_last_room"); }
    }).catch(() => {});
  }, [userProfile]);
  const [joinCode, setJoinCode] = useState("");
  const [showProfile, setShowProfile] = useState(false);
  const [showLobbyBrowser, setShowLobbyBrowser] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showFriends, setShowFriends] = useState(false);
  const [showDaily, setShowDaily] = useState(false);
  const [showWallet, setShowWallet] = useState(false);
  const [showAchievements, setShowAchievements] = useState(false);
  const [showNews, setShowNews] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [showLongNardy, setShowLongNardy] = useState(false);

  const joinByCode = () => {
    const code = joinCode.trim().toUpperCase();
    if (code.length >= 4) navigate("/" + code);
  };

  const createLocalGame = (mode: GameMode = GameMode.Backgammon) => {
    if (isCreatingLobby) return;
    resetLocalStore(dispatch, true);
    dispatch(setGameMode(mode));
    dispatch(setShowMatchSetupScreen(true));
    dispatch(setWipeTransition(true));
    navigate("/local");
  };

  const closeActiveRoom = async () => {
    const code = activeGameCode || localStorage.getItem("ng_last_room");
    if (!code) return;
    setIsDeletingRoom(true);
    try {
      await deleteLobbyByCode(code);
      localStorage.removeItem("ng_last_room");
      setActiveGameCode(null);
    } catch (err) {
      console.error("Failed to delete room:", err);
    } finally {
      setIsDeletingRoom(false);
    }
  };

  const createOnlineLobby = async (mode: GameMode = GameMode.Backgammon) => {
    if (isCreatingLobby || !userProfile) return;

    // Если есть активная комната — переходим в неё, а не создаём новую
    const existingCode = activeGameCode || localStorage.getItem("ng_last_room");
    if (existingCode) {
      // Проверяем что комната ещё существует
      const docRef = await findLobby(existingCode);
      if (docRef) {
        dispatch(setWipeTransition(true));
        navigate("/" + existingCode);
        return;
      }
      // Комната больше не существует — чистим
      localStorage.removeItem("ng_last_room");
      setActiveGameCode(null);
    }

    setIsCreatingLobby(true);
    dispatch(setGameMode(mode));
    try {
      const result = await createLobby(
        { uid: userProfile.uid, nickname: userProfile.nickname, rating: userProfile.rating },
        0,
        mode
      );
      try {
        await createPublicLobby(
          result.roomCode,
          userProfile.nickname,
          userProfile.rating,
          1,     // default points to win
          false, // doubling off by default
          0,     // no bet by default
          mode
        );
      } catch (err) {
        console.error("Failed to create public lobby:", err);
      }
      dispatch(setShowMatchSetupScreen(true));
      dispatch(setWipeTransition(true));
      navigate("/" + result.roomCode);
    } finally {
      setIsCreatingLobby(false);
    }
  };

  return (
    <>
      <div className="MM-wrapper">

        {/* ── Верхняя панель ── */}
        <div className="MM-topbar">

          {/* Левая часть — профиль */}
          {userProfile ? (
            <button className="MM-profile-btn" onClick={() => setShowProfile(true)}>
              <div className="MM-avatar">{userProfile.nickname.charAt(0).toUpperCase()}</div>
              <div className="MM-profile-info">
                <span className="MM-profile-nick">{userProfile.nickname}</span>
                <span className="MM-profile-rating">
                  {userProfile.rating} · {getRankLabel(userProfile.rating)}
                </span>
              </div>
            </button>
          ) : (
            <div className="MM-profile-empty">Гость</div>
          )}

          {/* Центр — монеты + пополнить */}
          {userProfile && (
            <button className="MM-coins" onClick={() => setShowWallet(true)}>
              <div className="MM-coins-dot" />
              <span className="MM-coins-val">{userProfile.coins.toLocaleString()} ₽</span>
              <span className="MM-coins-plus">+</span>
            </button>
          )}

          {/* Правая часть — навигация */}
          <div className="MM-nav">
            <button className="MM-nav-btn MM-nav-btn-daily" onClick={() => setShowDaily(true)}>Бонус</button>
            <button className="MM-nav-btn" onClick={() => setShowAchievements(true)}>Достижения</button>
            <button className="MM-nav-btn" onClick={() => setShowLeaderboard(true)}>Рейтинг</button>
            <button className="MM-nav-btn" onClick={() => setShowFriends(true)}>Друзья</button>
            <button className="MM-nav-btn MM-nav-btn-news" onClick={openNews}>
              Новости
              {hasUnreadNews && <span className="MM-news-dot" />}
            </button>
            <button className="MM-nav-btn MM-nav-btn-support" onClick={() => setShowSupport(true)}>Поддержка</button>
          </div>
        </div>

        {/* ── Заголовок ── */}
        <div className="MM-title">НАРДЫ</div>
        <div className="MM-subtitle">Классическая игра</div>

        {/* ── Кнопки игры ── */}
        <div className="MM-game-buttons">

          {/* Возврат в активную игру */}
          {activeGameCode && (
            <div className="MM-active-room">
              <button
                className="MM-game-btn MM-return-btn"
                onClick={() => { dispatch(setWipeTransition(true)); navigate("/" + activeGameCode); }}
              >
                <div className="MM-game-btn-icon" style={{background:"rgba(255,200,50,0.12)",border:"1px solid rgba(255,200,50,0.3)",color:"rgba(255,200,50,0.9)",fontSize:"1.2rem",fontWeight:900}}>&#9889;</div>
                <div className="MM-game-btn-body">
                  <div className="MM-game-btn-title" style={{color:"#ffc832"}}>Вернуться в игру</div>
                  <div className="MM-game-btn-sub">Ваша партия ещё активна &middot; {activeGameCode}</div>
                </div>
              </button>
              <button
                className="MM-close-room-btn"
                onClick={closeActiveRoom}
                disabled={isDeletingRoom}
                title="Закрыть комнату"
              >
                {isDeletingRoom ? "..." : "\u2715"}
              </button>
            </div>
          )}

          <button className="MM-game-btn" onClick={() => createLocalGame()}>
            <div className="MM-game-btn-icon MM-icon-local">
              <div className="MM-icon-checker" />
              <div className="MM-icon-checker" />
            </div>
            <div className="MM-game-btn-body">
              <div className="MM-game-btn-title">Игра с ботом</div>
              <div className="MM-game-btn-sub">Тренировка против компьютера</div>
            </div>
          </button>

          <button
            className="MM-game-btn MM-game-btn-online"
            onClick={() => createOnlineLobby()}
            disabled={isCreatingLobby}
          >
            <div className="MM-game-btn-icon MM-icon-online">
              {isCreatingLobby ? (
                <div className="Btn-spinner" />
              ) : (
                <div className="MM-icon-signal">
                  <span /><span /><span />
                </div>
              )}
            </div>
            <div className="MM-game-btn-body">
              <div className="MM-game-btn-title">
                {isCreatingLobby ? "Создание комнаты..." : "Онлайн игра"}
              </div>
              <div className="MM-game-btn-sub">Игра с соперником через интернет</div>
            </div>
          </button>

          <button className="MM-browse-btn" onClick={() => setShowLobbyBrowser(true)}>
            Найти открытую игру
          </button>

          {/* Длинные нарды — выбор режима теперь в настройках матча */}

          {/* 21 очко */}
          <button className="MM-game-btn MM-game-btn-long" disabled>
            <div className="MM-game-btn-icon MM-icon-long" style={{background:"rgba(255,200,50,0.08)", border:"1px solid rgba(255,200,50,0.15)"}}>
              <span style={{fontSize:"1.1rem", fontWeight:900, color:"rgba(255,200,50,0.7)"}}>21</span>
            </div>
            <div className="MM-game-btn-body">
              <div className="MM-game-btn-title">21 очко</div>
              <div className="MM-game-btn-sub">Новый режим — наберите 21 очко раньше соперника</div>
            </div>
            <span className="MM-soon-badge">Скоро</span>
          </button>

          {/* Ввод кода комнаты */}
          <div className="MM-join-row">
            <input
              className="MM-join-input"
              type="text"
              placeholder="Код комнаты"
              value={joinCode}
              maxLength={8}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === "Enter") joinByCode(); }}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
            />
            <button
              className="MM-join-btn"
              onClick={joinByCode}
              disabled={joinCode.trim().length < 4}
            >
              Войти
            </button>
          </div>

        {/* Контакт — подтягивается из админки */}
        {contact && contact.telegram && (
          <div className="MM-contact-bar">
            <a className="MM-contact-link" href={contact.telegram} target="_blank" rel="noopener noreferrer">
              Поддержка
            </a>
          </div>
        )}

        </div>
      </div>

      {showProfile    && <UserProfilePanel onClose={() => setShowProfile(false)} />}
      {showLobbyBrowser && <LobbyBrowser onClose={() => setShowLobbyBrowser(false)} />}
      {showLeaderboard  && <LeaderboardPanel onClose={() => setShowLeaderboard(false)} />}
      {showFriends && (
        <FriendsPanel
          onClose={() => setShowFriends(false)}
          onInvite={(code) => navigate(`/${code}`)}
        />
      )}
      {showDaily   && <DailyBonus onClose={() => setShowDaily(false)} />}
      {showWallet  && <WalletPanel onClose={() => setShowWallet(false)} />}
      {showAchievements && <AchievementsPanel onClose={() => setShowAchievements(false)} />}
      {showNews    && <NewsPanel onClose={() => setShowNews(false)} />}
      {showSupport && <SupportPanel onClose={() => setShowSupport(false)} />}
      {showLongNardy && (
        <div className="Overlay" onClick={() => setShowLongNardy(false)}>
          <div className="Modal Long-nardy-modal" onClick={(e) => e.stopPropagation()}>
            <div className="Modal-header">
              <div className="Modal-title">Длинные нарды</div>
              <button className="Modal-close" onClick={() => setShowLongNardy(false)}>✕</button>
            </div>
            <div className="Long-nardy-content">
              <div className="Long-nardy-icon">🎲</div>
              <p>Длинные нарды (классические) — в разработке.</p>
              <p>В этом варианте все 15 шашек начинают с головы, бить шашки соперника нельзя.</p>
              <p className="Long-nardy-soon">Ожидайте в следующем обновлении!</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MainMenu;
